import type { AddressMapMarker } from '@src/components/AddressMapViewer';
import {
  ATTENDANCE_TYPE,
  JOURNEY_ATTENDANCE_TYPES,
  type AttendanceEventSummary,
  type AttendanceType,
} from '@src/models/interfaces/attendanceEvent.interface';

import type { AttendanceMapCoords } from './resolveAttendanceAddress';

const MARK_LABEL_ID_BY_TYPE: Partial<Record<AttendanceType, string>> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'workJourney.management.matrix.attendance.mark.start',
  [ATTENDANCE_TYPE.BREAK_START]: 'workJourney.management.matrix.attendance.mark.breakStart',
  [ATTENDANCE_TYPE.BREAK_END]: 'workJourney.management.matrix.attendance.mark.breakEnd',
  [ATTENDANCE_TYPE.SHIFT_END]: 'workJourney.management.matrix.attendance.mark.end',
};

const JOURNEY_TYPE_SET = new Set<AttendanceType>(JOURNEY_ATTENDANCE_TYPES);

export type BuildAttendanceLocationMarkersParams = {
  workplace?: AttendanceMapCoords | null;
  workplaceLabel?: string | null;
  events?: readonly AttendanceEventSummary[] | null;
  formatMarkLabel: (attendanceType: AttendanceType) => string;
};

/**
 * Builds map markers for the attendance drawer:
 * - workplace (office icon) from the attendance record address
 * - one mark pin per journey attendance event that has GPS `position`
 */
export const buildAttendanceLocationMarkers = (
  params: BuildAttendanceLocationMarkersParams,
): AddressMapMarker[] => {
  const markers: AddressMapMarker[] = [];

  if (params.workplace) {
    markers.push({
      key: 'workplace',
      latitude: params.workplace.latitude,
      longitude: params.workplace.longitude,
      variant: 'workplace',
      label: params.workplaceLabel || undefined,
    });
  }

  const events = params.events || [];
  events.forEach(event => {
    if (!JOURNEY_TYPE_SET.has(event.attendanceType)) return;
    const position = event.markPosition;
    if (!position) return;

    markers.push({
      key: `mark-${event.id}`,
      latitude: position.lat,
      longitude: position.lon,
      variant: 'mark',
      label: params.formatMarkLabel(event.attendanceType),
    });
  });

  return markers;
};

export const getAttendanceMarkLabelId = (attendanceType: AttendanceType): string | null =>
  MARK_LABEL_ID_BY_TYPE[attendanceType] ?? null;
